import React from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native'
import { Home, Package, ShoppingCart, Activity, Menu } from 'lucide-react-native'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useLanguage } from '@fliisi/i18n'
import { useNavShell } from '../contexts/NavShellContext'
import theme from '../theme'

const { width } = Dimensions.get('window')
const isDesktop = width > 768

interface Props {
  storeId: string
  pendingOffersCount?: number
}

type NavItem = {
  key: string
  icon: React.ComponentType<{ size: number; color: string }>
  labelKey: string
  route: keyof RootStackParamList
  getParams?: () => object
  badge?: number
}

export default function MerchantBottomNav({ storeId, pendingOffersCount = 0 }: Props) {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { routeName } = useNavShell()
  const { t } = useLanguage()

  const navItems: NavItem[] = [
    {
      key: 'home',
      icon: Home,
      labelKey: 'home',
      route: 'MerchantDashboard',
      getParams: () => ({ storeId }),
    },
    {
      key: 'inventory',
      icon: Package,
      labelKey: 'inventory',
      route: 'StoreInventory',
      getParams: () => ({ storeId }),
    },
    {
      key: 'pos',
      icon: ShoppingCart,
      labelKey: 'pos',
      route: 'MerchantPOS',
      getParams: () => ({ storeId }),
    },
    {
      key: 'activity',
      icon: Activity,
      labelKey: 'activity',
      route: 'MerchantActivity',
      getParams: () => ({ storeId }),
      badge: pendingOffersCount > 0 ? pendingOffersCount : undefined,
    },
    {
      key: 'more',
      icon: Menu,
      labelKey: 'more',
      route: 'MerchantMore',
      getParams: () => ({ storeId }),
    },
  ]

  const isActive = (item: NavItem) => {
    // Group related screens under their nav item
    if (item.key === 'home' && routeName === 'MerchantDashboard') return true
    if (item.key === 'inventory' && ['StoreInventory', 'StoreCamera', 'MerchantProductEdit', 'CategoryManagement', 'StockAdjustment'].includes(routeName)) return true
    if (item.key === 'sell' && routeName === 'MerchantPOS') return true
    if (item.key === 'activity' && ['MerchantActivity', 'IncomingOffers'].includes(routeName)) return true
    if (item.key === 'more' && ['MerchantMore', 'StoreSettings', 'MerchantStaff', 'InventoryReports', 'SupplierManagement', 'PurchaseOrders', 'PurchaseOrderDetail'].includes(routeName)) return true
    return false
  }

  const handleNavPress = (item: NavItem) => {
    const params = item.getParams ? item.getParams() : undefined
    navigation.navigate(item.route as any, params as any)
  }

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      <View style={[styles.nav, isDesktop && styles.navDesktop]}>
        {navItems.map((item) => {
          const active = isActive(item)
          const IconComponent = item.icon
          const iconColor = active ? theme.colors.merchant.primary : theme.colors.text.secondary
          return (
            <TouchableOpacity
              key={item.key}
              style={[styles.navItem, active && styles.navItemActive]}
              onPress={() => handleNavPress(item)}
            >
              <View style={styles.iconContainer}>
                <IconComponent size={22} color={iconColor} />
                {item.badge && item.badge > 0 && (
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </Text>
                  </View>
                )}
              </View>
              <Text style={[styles.navLabel, active && styles.navLabelActive]}>
                {t(item.labelKey)}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.merchant.border,
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
    ...(Platform.OS === 'web' ? { boxShadow: '0 -2px 8px rgba(192,122,95,0.1)' } : {
      shadowColor: theme.colors.merchant.primary,
      shadowOffset: { width: 0, height: -2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  containerDesktop: {
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.merchant.border,
    paddingBottom: 0,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 8px rgba(192,122,95,0.1)' } : {
      shadowOffset: { width: 0, height: 2 },
    }),
  },
  nav: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 8,
  },
  navDesktop: {
    justifyContent: 'center',
    gap: 24,
    maxWidth: 700,
    marginHorizontal: 'auto',
  },
  navItem: {
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 56,
  },
  navItemActive: {
    // Active state handled by text styles
  },
  iconContainer: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -10,
    backgroundColor: '#EF4444',
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  navLabel: {
    fontSize: 10,
    color: theme.colors.text.secondary,
    fontWeight: '500',
    marginTop: 2,
  },
  navLabelActive: {
    color: theme.colors.merchant.primary,
    fontWeight: '600',
  },
})
